import { cn } from "@pma/utils/helpers"
import { ChevronDown, Search } from "lucide-react"
import { ComponentPropsWithoutRef, Dispatch, SetStateAction, useEffect, useState } from "react"
import { format } from "date-fns"
import { DateRange } from "react-day-picker"
import { useQuery } from "@tanstack/react-query"
import { parseCookies } from "nookies"
import { ACCESS_TOKEN } from "@pma/utils/constants"
import axios from "axios"
import { GET_FILTERS_BY_PROJECT, SERVER_URL } from "@pma/utils/api-routes"
import { DictType } from "@pma/types"
import { Calendar } from "../calendar"
import useTranslator from "@pma/hooks/use-translator"
import { he } from "date-fns/locale"
import { Popover, PopoverClose, PopoverContent, PopoverTrigger } from "../popover"
import { Button } from "../button"
import { toast } from "@pma/hooks/use-toast"
import { AutoCompleteInput } from "./work-log-form/auto-completeInput"
import { Switch } from "../switch"

interface FilterSelectProps extends ComponentPropsWithoutRef<"select"> {
  label: string
}

function FilterSelect({ label, className, ...props }: FilterSelectProps) {
  return (
    <div className='relative'>
      <select
        className={cn(
          "w-full appearance-none rounded-lg border border-gray-200 bg-white px-4 py-2.5 text-sm text-gray-600 focus:border-blue-500 focus:outline-none",
          className,
        )}
        {...props}
      >
        {props.children}
      </select>
      <ChevronDown className='absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400' />
    </div>
  )
}

interface WorkLogsFilterProps {
  filters: {
    building: string
    supervisor: number
    type: string
  }
  onFilterChange: (filterName: "building" | "supervisor" | "type", value: string | number) => void
  project: DictType
  filterDateRange: DateRange | undefined
  setFilterDateRange: Dispatch<SetStateAction<DateRange | undefined>>
  search: string
  setSearch: Dispatch<SetStateAction<string>>
  setShowDeleted: Dispatch<SetStateAction<boolean>>
  showDeleted: boolean
}
export function WorkLogsFilter({
  search,
  setSearch,
  filterDateRange,
  setFilterDateRange,
  project,
  filters,
  onFilterChange,
  setShowDeleted,
  showDeleted
}: WorkLogsFilterProps) {
  const accessToken = parseCookies()[ACCESS_TOKEN]
  const [clearDate, setClearDate] = useState(false)
  const [localDate, setLocalDate] = useState<DateRange | undefined>(filterDateRange)

  const { translate, locale } = useTranslator()
  const isHebrew = locale === "he"
  const getFilterByOrg = async () => {
    const response = await axios.get(SERVER_URL + GET_FILTERS_BY_PROJECT, {
      params: {
        project_id: project?.id,
      },
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    })
    return response.data
  }
  const filterResult = useQuery({
    queryKey: ["filter-context"],
    queryFn: () => getFilterByOrg(),
  })

  const handleDateRangeChange = (range: DateRange | undefined) => {
    if (!range?.from || !range?.to) {
      setLocalDate(range)
      return
    }

    const maxRangeInDays = 92 // approx. 3 months
    const diffInTime = range.to.getTime() - range.from.getTime()
    const diffInDays = diffInTime / (1000 * 3600 * 24)

    if (diffInDays <= maxRangeInDays) {
      setLocalDate(range)
    } else {
      toast({
        title: translate("Error"),
        description: translate("Selected range exceeds 3 months"),
        variant: "error",
      })
      // Optionally show an error message here
      console.warn("Selected range exceeds 3 months")
    }
  }

  const handelDateSave = () => {
    if (localDate?.from && localDate.to) {
      setFilterDateRange(localDate)
    }
  }

  useEffect(() => {
    if (clearDate) {
      setClearDate(false)
      setFilterDateRange(undefined)
    }
  }, [clearDate])

  return (
    <div className='flex w-full flex-col gap-3 px-5 md:flex-row md:flex-wrap'>
      <div className='w-full md:min-w-[240px] md:flex-1'>
        <div className='relative'>
          <Search className={cn('absolute top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400', isHebrew ? "right-3" : "left-3")} />
          <input
            type='text'
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={`${translate("Search logs")}...`}
            className={cn(
              "w-full appearance-none rounded-lg border border-gray-200 bg-white px-10 py-2 text-sm text-gray-600 focus:border-blue-500 focus:outline-none",
            )}
          />
        </div>
      </div>
      <div className='w-full md:min-w-[240px] md:flex-1'>
        <AutoCompleteInput
          name='building'
          options={filterResult?.data?.numbers?.map((val: any) => {
            return { value: val, id: val }
          })}
          value={filters.building}
          onChange={(val) => onFilterChange("building", val)}
          useForm={false}
          placeholder={`${translate("Buildings")} / ${translate("Segments")}: ${translate("All")}`}
        />
      </div>

      <div className='w-full md:min-w-[240px] md:flex-1'>
        <AutoCompleteInput
          name='supervisor'
          options={filterResult?.data?.supervisors?.map((obj: any) => {
            return { value: obj.name, id: obj.id }
          })}
          value={filters.supervisor}
          onChange={(val) => onFilterChange("supervisor", val)}
          useForm={false}
          placeholder={`${translate("Supervisor")}: ${translate("All")}`}
        />
      </div>

      <div className='w-full md:min-w-[240px] md:flex-1'>
        <AutoCompleteInput
          name='type'
          options={filterResult?.data?.adresses?.map((val: any) => {
            return { value: val, id: val }
          })}
          value={filters.type}
          onChange={(val) => onFilterChange("type", val)}
          useForm={false}
          placeholder={translate("Address")}
        />
      </div>

      <div className='relative w-full md:min-w-[240px] md:flex-1'>
        <Popover>
          <PopoverTrigger asChild>
            <button className='flex w-full items-center justify-between rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm text-gray-600 hover:border-blue-500 focus:outline-none'>
              {localDate?.from ? (
                localDate.to ? (
                  <>
                    {format(localDate.from, "LLL dd, y", {
                      locale: isHebrew ? he : undefined,
                    })}{" "}
                    -{" "}
                    {format(localDate.to, "LLL dd, y", {
                      locale: isHebrew ? he : undefined,
                    })}
                  </>
                ) : (
                  format(localDate.from, "LLL dd, y", {
                    locale: isHebrew ? he : undefined,
                  })
                )
              ) : (
                <span className='text-sm'>{translate("Select date to filter")}</span>
              )}
            </button>
          </PopoverTrigger>
          {localDate?.from && (
            <button
              type='button'
              onClick={(e) => {
                e.stopPropagation() // prevent Popover from opening
                setLocalDate({ from: undefined, to: undefined })
                setClearDate(true)
              }}
              className='absolute right-2 top-1/2 -translate-y-1/2 transform text-gray-400 hover:text-gray-600'
            >
              &times;
            </button>
          )}
          <PopoverContent className='w-auto p-0' align='start'>
            <Calendar
              initialFocus
              mode='range'
              defaultMonth={new Date()}
              locale={isHebrew ? he : undefined}
              selected={localDate}
              onSelect={handleDateRangeChange}
              numberOfMonths={2}
            />
            <div className='flex w-full justify-center p-2'>
              <PopoverClose>
                <Button size={"sm"} className='w-[200px]' onClick={() => handelDateSave()}>
                  {translate("Save")}
                </Button>
              </PopoverClose>
            </div>
          </PopoverContent>
        </Popover>
      </div>

       <div className='flex items-center'>
          <div className='flex items-center gap-2'>
            <Switch
              checked={showDeleted}
              className='h-6 w-11'
              onCheckedChange={(checked) => setShowDeleted(checked)}
            />
            <h4 className='text-sm font-semibold'>{translate("Show Deleted Diary")}</h4>
          </div>
       </div>
    </div>
  )
}
